<template>
  <BaseModule
    v-bind="$props"
    class="single-promotion"
    data-module="single-promotion"
  >
    <Warning
      v-if="showWarning"
      :text="warningMessage"
    />

    <Loader v-if="loading" />

    <TextAndMedia
      v-else-if="selectedPromo"
      v-bind="$props"
      :text-position="textPosition === 'left' ? 'row' : 'row-reverse'"
      :title="selectedPromo.Nome"
      :subtitle="discountValue"
      :text="selectedPromo.Descricao"
      :get-button-model-prop="getButtonModel"
    >
      <template #title>
        <component
          :is="titleTag"
          class="tit tit-g"
        >
          {{ selectedPromo.Nome }}
        </component>
      </template>

      <template #content>
        <div
          v-if="benefitsList.length > 0"
          class="promo-benefits"
        >
          <ul
            class="benefits"
            :class="!isMobile ? 'benefits--two-columns' : ''"
          >
            <li
              v-for="item in benefitsList"
              :key="item.Codigo"
            >
              <LImage
                :src="item.ImagemDestaque"
                :alt="item.Descricao"
              />
              {{ item.Descricao }}
            </li>
          </ul>
        </div>
      </template>

      <template #footer>
        <div class="promo-prices">
          <p class="label">{{ $t('dailyRate') }}</p>

          <div class="prices-container">
            <span class="promo-price">
              {{ selectedPromo.Tarifa.ValorFormatado }}
            </span>
            <span class="price">
              {{ selectedPromo.Tarifa.ValorFormatadoSemDesconto }}
            </span>

            <div
              class="disclaimer"
              @mouseover="showTooltip"
              @mouseleave="hideTooltip"
            >
              <div
                ref="slb-tooltip"
                class="slb-tooltip"
              >
                <p>{{ selectedPromo.ItensDescricao.TipoPagamento }}</p>
                <p>{{ selectedPromo.ItensDescricao.Reembolsavel }}</p>
                <p>{{ selectedPromo.ItensDescricao.Pensao }}</p>
                <p>{{ selectedPromo.ItensDescricao.MinimoNoites }}</p>
              </div>
              <span class="info-icon"></span>
            </div>
          </div>
        </div>
      </template>
    </TextAndMedia>
  </BaseModule>
</template>

<script>
import debounce from 'lodash/debounce';

import base from '@/mixins/base';
import getApiHotels from '@/mixins/getApiHotels';
import handlePromos from '@/helpers/handlePromos';
import Loader from '@/components/Loader.vue';
import Warning from '@/components/Warning.vue';
import BaseModule from './generic/BaseModule.vue';
import TextAndMedia from './generic/TextAndMedia.vue';

export default {
  name: 'SinglePromotion',
  components: {
    BaseModule,
    TextAndMedia,
    Loader,
    Warning,
  },
  mixins: [getApiHotels, base],
  props: {
    ...BaseModule.props,
    ...TextAndMedia.props,
    hotelId: {
      type: Number,
      default: null,
    },
    promotionId: {
      type: Number,
      default: null,
    },
    buttons: {
      type: Array,
      default: () => [
        {
          buttonText: 'Reservar',
          buttonUsePromoLink: true,
          buttonUrl: {
            type: 'externalLink',
            value: '',
            openInNewTab: true,
          },
          buttonFeatured: true,
        },
      ],
    },
    textPosition: {
      type: String,
      default: 'right',
    },
  },
  data() {
    return {
      promotions: [],
      loading: false,
      maxBenefits: 6,
      isMobile: window.innerWidth < 992,
    };
  },
  computed: {
    selectedPromo() {
      return this.promotions.find((promo) => promo.Codigo === this.promotionId);
    },
    benefitsList() {
      if (this.selectedPromo && this.selectedPromo.ListaDestaquesYield) {
        const orderedList = [...this.selectedPromo.ListaDestaquesYield];

        return orderedList
          ?.sort((itemA, itemB) => itemB.Destaque - itemA.Destaque)
          .slice(0, this.maxBenefits);
      }

      return [];
    },
    showWarning() {
      const isCanvas = this.$parent === undefined;

      return (
        isCanvas &&
        !this.loading &&
        ((!this.hotelId && !this.promotionId) ||
          ((this.hotelId || this.promotionId) && !this.selectedPromo))
      );
    },
    warningMessage() {
      return !this.promotionId
        ? 'Selecione um hotel e uma promoção.'
        : 'Promoção não encontrada.';
    },
    discountValue() {
      return this.selectedPromo?.Tarifa.ExibirDesconto
        ? this.$tc('discount', this.selectedPromo.Tarifa.PercentualDesconto)
        : null;
    },
  },
  watch: {
    hotelId(value) {
      if (typeof value === 'number') {
        this.$store.commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
          path: 'data.promotionId',
          value: null,
        });
        this.promotions = [];
        this.requestPromotions();
      }
    },
  },
  mounted() {
    this.getApiHotels();

    if (this.hotelId) {
      this.requestPromotions();
    }

    window.addEventListener('resize', debounce(this.onResize, 150));
  },
  methods: {
    async requestPromotions() {
      this.loading = true;

      const platform = this.isMobile ? 'mobile' : 'desktop';
      const url = `${this.settings.letsbookApiId}/promocoes/${this.hotelId}/${platform}/${this.apiLocale}`;

      if (!this.apiData[url]) {
        try {
          await this.$store.dispatch('loadApiData', url);
        } catch {
          return;
        }
      }

      this.promotions = handlePromos(this.apiData[url]);
      this.loading = false;
    },
    getButtonModel({ buttonUsePromoLink, buttonUrl }) {
      return buttonUsePromoLink && this.selectedPromo
        ? this.selectedPromo.Tarifa.LinkPublico ||
            this.settings.letsbookUrl.replace(/\/admin\/?$/i, '/D/')
        : buttonUrl;
    },
    showTooltip(event) {
      const el = event.target;
      const parentPosition = el.parentElement.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      if (parentPosition.bottom <= viewportHeight - 200) {
        this.$refs['slb-tooltip'].classList.add('open-down');
      } else {
        this.$refs['slb-tooltip'].classList.add('open-up');
      }
    },
    hideTooltip() {
      this.$refs['slb-tooltip'].classList.remove('open-up', 'open-down');
    },
    onResize() {
      const isMobile = window.innerWidth < 992;

      if (this.isMobile !== isMobile) {
        this.isMobile = isMobile;
      }
    },
  },
  metadata: {
    extends: [BaseModule.metadata, TextAndMedia.metadata],
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'config',
            position: 0,
            fields: [
              {
                name: 'hotelId',
                position: 0,
                type: 'VueSelect',
                label: 'Selecione o hotel',
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Selecione um hotel',
                  },
                },
              },
              {
                name: 'promotionId',
                after: 'hotelId',
                type: 'VueSelect',
                label: 'Selecione a promoção',
                options: {
                  watch: true,
                  attribute: 'promotions',
                  handler(promotions) {
                    const promotionsOptions = promotions.map((promo) => ({
                      label: promo.Nome,
                      value: promo.Codigo,
                    }));
                    return promotionsOptions;
                  },
                },
                isEnabled({ hotelId }) {
                  return hotelId;
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Selecione uma promoção',
                  },
                },
              },
              {
                name: 'textPosition', // just updates options values
                position: 2,
                type: 'VueSelect',
                label: 'Posição do texto',
                options: [
                  {
                    label: 'Esquerda',
                    value: 'left',
                  },
                  {
                    label: 'Direita',
                    value: 'right',
                  },
                ],
              },
            ],
          },
          {
            name: 'buttons',
            label: 'Botões',
            fields: [
              {
                name: 'buttons',
                fields: [
                  {
                    name: 'buttonUsePromoLink',
                    before: 'buttonUrl',
                    type: 'Checkbox',
                    label: 'Usar link do Letsbook',
                  },
                  {
                    name: 'buttonUrl',
                    isEnabled() {
                      return !this.buttonUsePromoLink;
                    },
                  },
                ],
              },
            ],
          },
        ],
        toRemove: ['text'],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        dailyRate: 'Hospedagem a partir de',
        discount: 'Até {count}% OFF',
      },
      en: {
        dailyRate: 'Room rates from',
        discount: 'Up to {count}% OFF',
      },
      es: {
        dailyRate: 'Tarifas a partir de',
        discount: 'Hasta {count}% OFF',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.single-promotion {
  ::v-deep {
    .text-media .texto .txt .inner-text {
      min-height: unset;
    }
  }

  .promo-benefits {
    width: 100%;
    color: var(--text-color, $lb-medium-gray);

    .benefits {
      display: flex;
      flex-wrap: wrap;

      li {
        width: 100%;
        display: flex;
        align-items: flex-start;
        margin-bottom: 10px;

        .img {
          width: 24px;
          height: 24px;
          margin-right: 10px;
          flex-shrink: 0;
        }
      }

      &--two-columns {
        li {
          width: 50%;
        }
      }
    }
  }

  .promo-prices {
    width: 100%;
    min-width: 200px;
    margin: 8px 12px 16px 0;
    padding: 4px 0;

    .label {
      text-transform: uppercase;
      font-size: $size-normal;
    }

    .promo-price {
      font-size: 26px;
      font-weight: $lb-bold;
      color: var(--promo-color);
    }

    .price {
      color: $lb-gray;
      margin-bottom: 0.2em;
      margin-left: 15px;
      text-decoration: line-through;
    }

    .disclaimer {
      display: inline-block;
      margin-left: 15px;
      position: relative;

      .slb-tooltip {
        right: -10px;
        visibility: hidden;
        opacity: 0;
        color: $lb-dark-gray;

        &.open-up,
        &.open-down {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
      }
    }
  }

  .info-icon {
    line-height: 1em;
  }
}
</style>
